import {
  ChangeEvent,
  ComponentProps,
  FocusEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CalendarIcon } from 'lucide-react';
import { clsx } from 'clsx';
import { Calendar } from '@/components/ui/Calendar.tsx';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover.tsx';
import PrimitiveInput, {
  PrimitiveInputSize,
} from '@/components/form/_primitives/PrimitiveInput.tsx';
import CalendarMonthPicker from '@/components/form/_primitives/date/CalendarMonthPicker.tsx';
import CalendarYearPicker from '@/components/form/_primitives/date/CalendarYearPicker.tsx';
import MobileDatePickerDrawer from '@/components/form/_primitives/date/MobileDatePickerDrawer.tsx';
import { useIsMobile } from '@/hooks/use-mobile.tsx';
import {
  applyDateMask,
  clampDayToMonth,
  formatDdMmYyyy,
  parseStrictDdMmYyyy,
  POLISH_MONTHS,
} from '@/components/form/_primitives/date/dateInputUtils.ts';

type CalendarPassthroughProps = Omit<
  ComponentProps<typeof Calendar>,
  | 'mode'
  | 'selected'
  | 'onSelect'
  | 'month'
  | 'onMonthChange'
  | 'captionLayout'
  | 'components'
>;

export type PrimitiveDateInputProps = {
  value?: Date;
  onChange?: (value: Date | undefined) => void;
  error?: string;
  calendarOptions?: CalendarPassthroughProps;
  /** Tytuł arkusza z kalendarzem poniżej `md`; zwykle etykieta pola. */
  drawerTitle?: string;
  /** 30 px zamiast 34 px — wyłącznie dla kontrolek paska narzędzi tabeli. */
  inputSize?: PrimitiveInputSize;
} & Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'type'>;

const PrimitiveDateInput = ({
  value,
  onChange,
  error,
  calendarOptions,
  drawerTitle,
  disabled,
  className,
  placeholder,
  onBlur,
  onClick,
  onFocus,
  ...rest
}: PrimitiveDateInputProps) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [monthOpen, setMonthOpen] = useState(false);
  const [yearOpen, setYearOpen] = useState(false);
  const [text, setText] = useState(() => formatDdMmYyyy(value));
  const [viewMonth, setViewMonth] = useState<Date>(
    value ?? calendarOptions?.defaultMonth ?? new Date(),
  );
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const parsed = parseStrictDdMmYyyy(text);
    const sameDate =
      (value === undefined && parsed === undefined) ||
      (value !== undefined &&
        parsed !== undefined &&
        value.getTime() === parsed.getTime());
    if (!sameDate) {
      setText(formatDdMmYyyy(value));
    }
    if (value && value.getTime() !== viewMonth.getTime()) {
      const sameMonth =
        value.getFullYear() === viewMonth.getFullYear() &&
        value.getMonth() === viewMonth.getMonth();
      if (!sameMonth) setViewMonth(value);
    }
    // We deliberately do not depend on text/viewMonth — they're outputs of this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const restorePartialText = () => {
    const trimmedLength = text.length;
    if (trimmedLength === 0) return;
    if (trimmedLength < 10 || !parseStrictDdMmYyyy(text)) {
      setText(formatDdMmYyyy(value));
    }
  };

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    const masked = applyDateMask(e.target.value);
    setText(masked);
    if (masked.length === 10) {
      const parsed = parseStrictDdMmYyyy(masked);
      if (parsed) {
        setViewMonth(parsed);
        onChange?.(parsed);
      } else {
        onChange?.(undefined);
      }
    } else if (masked === '') {
      onChange?.(undefined);
    }
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    restorePartialText();
    onBlur?.(e);
  };

  const handleOpenChange = (next: boolean) => {
    if (disabled) return;
    setOpen(next);
    if (!next) restorePartialText();
  };

  // Poniżej `md` samo pole jest przyciskiem arkusza: tap i fokus (Enter z
  // poprzedniego pola) otwierają kalendarz, a `readOnly` trzyma klawiaturę
  // w kieszeni — datę wybiera się z kafli, nie wpisuje.
  const handleFieldClick = (e: MouseEvent<HTMLInputElement>) => {
    onClick?.(e);
    if (isMobile) handleOpenChange(true);
  };

  const handleFieldFocus = (e: FocusEvent<HTMLInputElement>) => {
    onFocus?.(e);
    if (isMobile) handleOpenChange(true);
  };

  const handleDaySelect = (date: Date | undefined) => {
    onChange?.(date);
    setOpen(false);
  };

  const handleMonthPick = (monthIdx: number) => {
    setMonthOpen(false);
    const year = viewMonth.getFullYear();
    setViewMonth(new Date(year, monthIdx, 1));
    if (value) {
      onChange?.(clampDayToMonth(year, monthIdx, value.getDate()));
    }
  };

  const handleYearPick = (year: number) => {
    setYearOpen(false);
    const monthIdx = viewMonth.getMonth();
    setViewMonth(new Date(year, monthIdx, 1));
    if (value) {
      onChange?.(clampDayToMonth(year, value.getMonth(), value.getDate()));
    }
  };

  const input = (
    <PrimitiveInput
      {...rest}
      ref={inputRef}
      type="text"
      inputMode={isMobile ? 'none' : 'numeric'}
      readOnly={isMobile}
      autoComplete="off"
      placeholder={placeholder ?? 'DD.MM.YYYY'}
      error={error}
      disabled={disabled}
      value={text}
      onChange={handleTextChange}
      onBlur={handleBlur}
      onClick={handleFieldClick}
      onFocus={handleFieldFocus}
      className={clsx(
        'pr-6',
        isMobile && !disabled && 'cursor-pointer caret-transparent',
        className,
      )}
    />
  );

  const calendarButtonClass =
    'absolute right-1 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-sm p-1 text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 disabled:cursor-not-allowed disabled:opacity-50';

  // Wpisywanie cyfr zostaje takie samo na obu szerokościach; tylko kalendarz
  // zmienia opakowanie — popover pod polem albo arkusz od dołu.
  if (isMobile) {
    return (
      <>
        <div className="relative w-full">
          {input}
          <button
            type="button"
            disabled={disabled}
            aria-label="Otwórz kalendarz"
            className={calendarButtonClass}
            onClick={() => handleOpenChange(true)}
          >
            <CalendarIcon size={18} />
          </button>
        </div>
        <MobileDatePickerDrawer
          open={open}
          onOpenChange={handleOpenChange}
          title={drawerTitle}
          value={value}
          viewMonth={viewMonth}
          onViewMonthChange={setViewMonth}
          onSelect={handleDaySelect}
          onMonthPick={handleMonthPick}
          onYearPick={handleYearPick}
          calendarOptions={calendarOptions}
        />
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <div className="relative w-full">
          {input}
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label="Otwórz kalendarz"
              className={calendarButtonClass}
            >
              <CalendarIcon size={18} />
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
        <Calendar
          {...calendarOptions}
          mode="single"
          captionLayout="label"
          month={viewMonth}
          onMonthChange={setViewMonth}
          selected={value}
          onSelect={handleDaySelect}
          components={{
            MonthCaption: ({ calendarMonth }) => (
              <CustomCaption
                month={calendarMonth.date}
                monthOpen={monthOpen}
                yearOpen={yearOpen}
                onMonthOpenChange={setMonthOpen}
                onYearOpenChange={setYearOpen}
                onMonthPick={handleMonthPick}
                onYearPick={handleYearPick}
              />
            ),
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

type CustomCaptionProps = {
  month: Date;
  monthOpen: boolean;
  yearOpen: boolean;
  onMonthOpenChange: (open: boolean) => void;
  onYearOpenChange: (open: boolean) => void;
  onMonthPick: (monthIdx: number) => void;
  onYearPick: (year: number) => void;
};

const CustomCaption = ({
  month,
  monthOpen,
  yearOpen,
  onMonthOpenChange,
  onYearOpenChange,
  onMonthPick,
  onYearPick,
}: CustomCaptionProps) => {
  const monthIdx = month.getMonth();
  const year = month.getFullYear();
  const triggerClass =
    'pointer-events-auto rounded-sm px-2 py-1 text-sm font-medium text-neutral-700 hover:bg-neutral-100';

  return (
    <div className="pointer-events-none relative z-10 flex items-center justify-center gap-1">
      <Popover open={monthOpen} onOpenChange={onMonthOpenChange}>
        <PopoverTrigger asChild>
          <button type="button" className={triggerClass}>
            {POLISH_MONTHS[monthIdx]}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="center"
          sideOffset={6}
          className="w-[220px] p-1.5"
        >
          <CalendarMonthPicker currentMonth={monthIdx} onPick={onMonthPick} />
        </PopoverContent>
      </Popover>
      <Popover open={yearOpen} onOpenChange={onYearOpenChange}>
        <PopoverTrigger asChild>
          <button type="button" className={triggerClass}>
            {year}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="center"
          sideOffset={6}
          className="w-[200px] p-1.5"
        >
          <CalendarYearPicker currentYear={year} onPick={onYearPick} />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default PrimitiveDateInput;
